// src/pages/Character.tsx
import React, { useState, useEffect } from 'react'
import api from '../api/axios'
import './Character.css'

import tabBtnImg      from '../assets/character/button.png'
import pepeImg        from '../assets/character/pepe.png'
import boxImg         from '../assets/character/box.png'
import placeholderImg from '../assets/character/placeholder.png'

type Inventory = {
  equipment: Record<string, { level?: number; stats?: Record<string, number> }>
  weapon:    Record<string, { level?: number; stats?: Record<string, number> }>
  materials: Record<string, number>
  items:     Record<string, number>
  tools:     Record<string, { level: number; stats?: Record<string, number> }>
}

type Stats = {
  name?:      string
  level:      number
  xp:         number
  xp_to_next: number
  health:     number
  max_health: number
  damage:     number
  defense:    number
  speed:      number
  silver:     number
  bank:       number
  gold:       number
}

export type EquippedState = {
  head?:       string
  chestplate?: string
  pants?:      string
  boots?:      string
  weapon?:     string
}

interface CharacterResponse extends Stats {
  inventory: Inventory
  equipped:  EquippedState
}

// prettify keys like "small_health_potion" → "Small HP Potion"
const prettifyName = (key: string): string => {
  const capitalize = (s: string) =>
    s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()

  return key
    .split('_')
    .map(tok => {
      if (tok === 'health' || tok.toLowerCase() === 'hp') return 'HP'
      return capitalize(tok)
    })
    .join(' ')
}

export default function CharacterPage() {
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState<string | null>(null)
  const [stats, setStats]           = useState<Stats | null>(null)
  const [inv, setInv]               = useState<Inventory | null>(null)
  const [equipped, setEquipped]     = useState<EquippedState>({})
  const [activeTab, setActiveTab]   = useState<'character'|'inventory'|'usable'>('character')
  const [confirmItem, setConfirmItem] = useState<string|null>(null)

  const loadCharacter = () => {
    api.get<CharacterResponse>('/character')
      .then(res => {
        const { inventory, equipped, ...rest } = res.data
        setStats(rest)
        setInv(inventory)
        setEquipped(equipped)
      })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false))
  }

  useEffect(loadCharacter, [])

  const makeTooltip = (statsObj?: Record<string, number>, level?: number) => {
    const lines: string[] = []
    if (level !== undefined) lines.push(`Level: ${level}`)
    if (statsObj) Object.entries(statsObj).forEach(([k,v]) => lines.push(`${prettifyName(k)}: ${v}`))
    return lines.join('\n')
  }

  const doUseItem = () => {
    if (!confirmItem) return
    api.post('/inventory/use', { item: confirmItem })
      .then(loadCharacter)
      .catch(err => alert(err.response?.data?.error || err.message))
      .finally(() => setConfirmItem(null))
  }

  const handleEquip = (slot: keyof EquippedState, key: string) => {
    api.post('/inventory/equip', { slot, itemKey: key })
      .then(() => {
        setEquipped(e => ({ ...e, [slot]: key }))
        loadCharacter()
      })
      .catch(err => alert(err.response?.data?.error || err.message))
  }

  const isEquipped = (slot: keyof EquippedState, key: string) =>
    equipped[slot] === key

  const imgFor = (key: string) => {
    try {
      return require(`../assets/character/${key}.png`)
    } catch {
      return placeholderImg
    }
  }

  // partition equipment
  const equipment = inv?.equipment ?? {}
  const entries    = Object.entries(equipment)
  const headItems  = entries.filter(([k]) => /hood|cowl|helmet/.test(k))
  const chestItems = entries.filter(([k]) => /robe|tunic|armor|shirt/.test(k))
  const pantsItems = entries.filter(([k]) => /pants|trousers/.test(k))
  const bootsItems = entries.filter(([k]) => /boots|sandals/.test(k))
  const equipmentSlots: Array<[string, [string, any][], keyof EquippedState]> = [
    ['Head',       headItems,  'head'],
    ['Chestplate', chestItems, 'chestplate'],
    ['Pants',      pantsItems, 'pants'],
    ['Boots',      bootsItems, 'boots'],
  ]

  return (
    <div className="character-page">
      <div className="character-container">

        {/* Tabs */}
        <div className="tab-bar">
          {(['character','inventory','usable'] as const).map(tab => (
            <button
              key={tab}
              className={`tab-btn${activeTab===tab?' active':''}`}
              onClick={()=>setActiveTab(tab)}
              style={{ backgroundImage:`url(${tabBtnImg})` }}
            >
              {tab==='character' ? 'Character'
               : tab==='inventory'? 'Inventory'
               : 'Usable Items'}
            </button>
          ))}
        </div>

        {/* Loading/Error */}
        {loading && <p className="loading">Loading character…</p>}
        {!loading && error && <p className="error">{error}</p>}

        {/* Main */}
        {!loading && !error && stats && inv && (
          <>
            {/* Character Tab */}
            {activeTab==='character' && (
              <>
                <div className="avatar-container">
                  <img src={pepeImg} className="avatar" alt="Avatar"/>
                </div>
                <div className="info-box">
                  <img src={boxImg} className="box-bg" alt="Info Box"/>
                  <div className="info-content">
                    <h3 className="hero-name">{stats.name||'Hero'}</h3>
                    <p><strong>Level:</strong> {stats.level}</p>
                    <p><strong>XP:</strong> {stats.xp} / {stats.xp_to_next}</p>
                    <p><strong>HP:</strong> {stats.health} / {stats.max_health}</p>
                    <p><strong>Damage:</strong> {stats.damage}</p>
                    <p><strong>Defense:</strong> {stats.defense}</p>
                    <p><strong>Speed:</strong> {stats.speed}</p>
                    <p><strong>Silver:</strong> {stats.silver}</p>
                    <p><strong>Bank:</strong> {stats.bank}</p>
                    <p><strong>Gold:</strong> {stats.gold}</p>
                  </div>
                </div>
              </>
            )}

            {/* Inventory Tab */}
            {activeTab==='inventory' && (
              <section className="inventory-panel">
                {equipmentSlots.map(([label, items, slot])=>(
                  <div key={slot} className="slot-section">
                    <h3>{label}</h3>
                    <ul className="inventory-grid">
                      {items.length
                        ? items.map(([key,val])=>(
                          <li key={key} className={`inventory-slot${isEquipped(slot,key)?' equipped':''}`}>
                            <div className="tooltip">
                              <img src={imgFor(key)} className="inventory-img" alt={key}/>
                              <span className="tooltiptext">{makeTooltip(val.stats,val.level)}</span>
                            </div>
                            <span className="inventory-label">{prettifyName(key)}</span>
                            <button className="equip-btn" onClick={()=>handleEquip(slot,key)}>
                              {isEquipped(slot,key)?'Equipped':'Equip'}
                            </button>
                          </li>
                        ))
                        : <li className="empty">—</li>
                      }
                    </ul>
                  </div>
                ))}

                <h3>Weapon</h3>
                <ul className="inventory-grid">
                  {Object.entries(inv.weapon ?? {}).map(([key,val])=>(
                    <li key={key} className={`inventory-slot${isEquipped('weapon',key)?' equipped':''}`}>
                      <div className="tooltip">
                        <img src={imgFor(key)} className="inventory-img" alt={key}/>
                        <span className="tooltiptext">{makeTooltip(val.stats,val.level)}</span>
                      </div>
                      <span className="inventory-label">{prettifyName(key)}</span>
                      <button className="equip-btn" onClick={()=>handleEquip('weapon',key)}>
                        {isEquipped('weapon',key)?'Equipped':'Equip'}
                      </button>
                    </li>
                  ))}
                </ul>

                <h3>Tools</h3>
                <ul className="inventory-grid">
                  {Object.entries(inv.tools ?? {}).map(([key,val])=>(
                    <li key={key} className="inventory-slot">
                      <div className="tooltip">
                        <img src={imgFor(key)} className="inventory-img" alt={key}/>
                        <span className="tooltiptext">{makeTooltip(val.stats,val.level)}</span>
                      </div>
                      <span className="inventory-label">{prettifyName(key)} (Lvl {val.level})</span>
                    </li>
                  ))}
                </ul>

                <h3>Materials</h3>
                <ul className="inventory-grid">
                  {Object.entries(inv.materials ?? {}).map(([mKey,mAmt])=>(
                    <li key={mKey} className="inventory-slot">
                      <div className="tooltip">
                        <img src={imgFor(mKey)} className="inventory-img" alt={mKey}/>
                      </div>
                      <span className="inventory-label">{prettifyName(mKey)}: {mAmt}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Usable Items Tab */}
            {activeTab==='usable' && (
              <section className="usable-panel">
                <h3>Usable Items</h3>
                <ul className="inventory-grid">
                  {Object.entries(inv.items ?? {}).map(([key,amt])=>(
                    <li key={key} className="inventory-slot">
                      <div className="tooltip">
                        <img src={imgFor(key)} className="inventory-img" alt={key}/>
                      </div>
                      <span className="inventory-label">{prettifyName(key)}: {amt}</span>
                      <button
                        className="use-btn"
                        onClick={()=>setConfirmItem(key)}
                        disabled={amt===0}
                        style={{ backgroundImage:`url(${tabBtnImg})` }}
                      >
                        Use
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmItem && (
        <div className="confirm-overlay">
          <div className="confirm-box">
            <p>Use one <strong>{prettifyName(confirmItem)}</strong>?</p>
            <div className="confirm-buttons">
              <button onClick={doUseItem}>Yes</button>
              <button onClick={()=>setConfirmItem(null)}>No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
